/**
 * 공용 날짜·시간 선택기 계산(docs/seminar-date-picker-plan.md — 세미나 폼에서 시작해 공용으로 옮김).
 * 날짜는 `YYYY-MM-DD`, 시각은 `HH:mm`, 일시는 `YYYY-MM-DDTHH:mm`(로컬, 시간대 없음) 문자열로 다룬다 — 폼 저장값과 같은 모양.
 */

/** 시간 버튼 범위 — 오전 7:00 ~ 오후 10:00, 30분 단위 */
const SLOT_FIRST_MINUTES: number = 7 * 60;
const SLOT_LAST_MINUTES: number = 22 * 60;
const SLOT_STEP_MINUTES: number = 30;
/** 직접 입력 단위(분) */
export const MANUAL_STEP_MINUTES: number = 10;
/** 고른 시간이 없을 때 시간 줄을 이 시각으로 넘겨 둔다 */
export const DEFAULT_SCROLL_TIME: string = '10:00';
/** 시간 버튼 범위 안내 */
export const SLOT_RANGE_TEXT: string = '오전 7:00 ~ 오후 10:00, 30분 단위';
/** 하루의 마지막 시각(직접 입력 상한) */
const LAST_MINUTE_OF_DAY: number = 23 * 60 + 50;

const WEEKDAYS: string[] = ['일', '월', '화', '수', '목', '금', '토'];

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Date → `YYYY-MM-DD` (로컬) */
export const formatDateOnly = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/** `YYYY-MM-DD` 또는 `YYYY-MM-DDTHH:mm[:ss]` → 로컬 Date. 형식이 틀리면 null */
export const parseLocal = (value?: string | null): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(value);
  if (!match) return null;
  const [, y, m, d, hh, mm] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d), Number(hh ?? 0), Number(mm ?? 0));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** `HH:mm` → 자정부터 분. 형식이 틀리면 NaN */
export const toMinutes = (time: string): number => {
  const match = /^(\d{2}):(\d{2})/.exec(time);
  return match ? Number(match[1]) * 60 + Number(match[2]) : Number.NaN;
};

/** 자정부터 분 → `HH:mm` */
export const fromMinutes = (minutes: number): string => `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;

/** 시간 버튼 31개(`07:00` ~ `22:00`) */
export const TIME_SLOTS: string[] = Array.from(
  { length: (SLOT_LAST_MINUTES - SLOT_FIRST_MINUTES) / SLOT_STEP_MINUTES + 1 },
  (_, i) => fromMinutes(SLOT_FIRST_MINUTES + i * SLOT_STEP_MINUTES),
);

/** 시간 버튼에 있는 시각인지(없으면 열 때 직접 입력 상태로 연다) */
export const isSlotTime = (time: string): boolean => TIME_SLOTS.includes(time);

/** 직접 입력 시각이 10분 단위인지 */
export const isManualStep = (time: string): boolean => {
  const minutes: number = toMinutes(time);
  return !Number.isNaN(minutes) && minutes % MANUAL_STEP_MINUTES === 0;
};

/** `HH:mm` → { ampm: '오후', clock: '2:00' } — 12시는 오후 12:00 */
export const splitAmPm = (time: string): { ampm: string; clock: string } => {
  const minutes: number = toMinutes(time);
  const hour: number = Math.floor(minutes / 60);
  const hour12: number = hour % 12 === 0 ? 12 : hour % 12;
  return { ampm: hour < 12 ? '오전' : '오후', clock: `${hour12}:${pad2(minutes % 60)}` };
};

/** `HH:mm` → `오후 2:00` */
export const formatTimeKo = (time: string): string => {
  const { ampm, clock } = splitAmPm(time);
  return `${ampm} ${clock}`;
};

/** `YYYY-MM-DD…` → `10월 12일 (일)` */
export const formatDayKo = (value: string): string => {
  const date: Date | null = parseLocal(value);
  if (!date) return '';
  return `${date.getMonth() + 1}월 ${date.getDate()}일 (${WEEKDAYS[date.getDay()]})`;
};

/** `YYYY-MM-DD…` → `10/12(일)` (입력 칸 표시) */
export const formatDayShort = (value: string): string => {
  const date: Date | null = parseLocal(value);
  if (!date) return '';
  return `${date.getMonth() + 1}/${date.getDate()}(${WEEKDAYS[date.getDay()]})`;
};

/** 일시 문자열에서 날짜·시각 떼기 (`2026-10-12T14:00[:00]`) */
export const splitDateTime = (value?: string | null): { date: string | null; time: string | null } => {
  const match = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/.exec(value ?? '');
  if (!match) return { date: null, time: null };
  return { date: match[1], time: match[2] ?? null };
};

/** 날짜 + 시각 → `YYYY-MM-DDTHH:mm` */
export const joinDateTime = (date: string, time: string): string => `${date}T${time}`;

/** 두 날짜 사이 날 수(양 끝 포함) */
export const countDays = (start: string, end: string): number => {
  const a: Date | null = parseLocal(start);
  const b: Date | null = parseLocal(end);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000)) + 1;
};

/** `10월 12일 ~ 14일 (3일)` · 달이 바뀌면 `10월 30일 ~ 11월 2일 (4일)` · 같은 날이면 `10월 12일 (일) 하루` */
export const formatRangeKo = (start: string, end: string): string => {
  if (start === end) return `${formatDayKo(start)} 하루`;
  const a: Date | null = parseLocal(start);
  const b: Date | null = parseLocal(end);
  if (!a || !b) return '';
  const endText: string = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
    ? `${b.getDate()}일`
    : `${b.getMonth() + 1}월 ${b.getDate()}일`;
  return `${a.getMonth() + 1}월 ${a.getDate()}일 ~ ${endText} (${countDays(start, end)}일)`;
};

/** 그 달 1일(로컬) */
export const monthStart = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

/** 달 더하기(1일 기준) */
export const addMonths = (date: Date, months: number): Date => new Date(date.getFullYear(), date.getMonth() + months, 1);

/** 달력에 그릴 날 — 앞뒤 빈칸은 null, 한 줄 7칸 */
export const monthCells = (month: Date): Array<string | null> => {
  const first: Date = monthStart(month);
  const daysInMonth: number = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells: Array<string | null> = Array.from({ length: first.getDay() }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(formatDateOnly(new Date(first.getFullYear(), first.getMonth(), day)));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/** 일시 범위 검사 — min 보다 앞이거나 max 보다 뒤면 못 고른다 */
export const isDateTimeAllowed = (date: string, time: string, min?: Date | null, max?: Date | null): boolean => {
  const at: Date | null = parseLocal(joinDateTime(date, time));
  if (!at) return false;
  if (min && at < min) return false;
  if (max && at > max) return false;
  return true;
};

/** 그날 고를 수 있는 시각이 하나라도 있는지(직접 입력 포함 — 10분 단위로 본다) */
export const isDayAllowedForDateTime = (date: string, min?: Date | null, max?: Date | null): boolean =>
  isDateTimeAllowed(date, fromMinutes(LAST_MINUTE_OF_DAY), min, null) && isDateTimeAllowed(date, '00:00', null, max);

/** 시작 + 분(하루를 넘기지 않는다). 넘기면 null */
export const addMinutesSameDay = (time: string, minutes: number): string | null => {
  const total: number = toMinutes(time) + minutes;
  return Number.isNaN(total) || total > LAST_MINUTE_OF_DAY ? null : fromMinutes(total);
};

/** Date → 그 1분 뒤(종료는 시작보다 '뒤'여야 해서 하한으로 쓴다) */
export const oneMinuteAfter = (date: Date | null): Date | null => (date ? new Date(date.getTime() + 60 * 1000) : null);

/** 달력을 처음 펼칠 달 — 고른 날, 없으면 고를 수 있는 첫날(오늘보다 뒤면), 없으면 이번 달 */
export const initialMonth = (picked: string | null | undefined, lower?: Date | string | null): Date => {
  const pickedDate: Date | null = parseLocal(picked);
  if (pickedDate) return monthStart(pickedDate);
  const lowerDate: Date | null = typeof lower === 'string' ? parseLocal(lower) : lower ?? null;
  const now: Date = new Date();
  return monthStart(lowerDate && lowerDate > now ? lowerDate : now);
};

/** 오전/오후 */
export type AmPm = 'AM' | 'PM';

/** `HH:mm` → 오전/오후 */
export const ampmOf = (time: string): AmPm => (toMinutes(time) < 12 * 60 ? 'AM' : 'PM');

/** 오전 칸 = 오전 7:00~11:30, 오후 칸 = 오후 12:00~10:00 (버튼 범위 그대로) */
export const slotsOf = (ampm: AmPm): string[] => TIME_SLOTS.filter((time) => ampmOf(time) === ampm);

/** 시작 + 2시간(같은 날 23:50 까지). 넘기면 null — 종료 칸 지름길·회차 종료 미리 선택 */
export const plusTwoHours = (time: string | null | undefined): string | null => (time ? addMinutesSameDay(time, 120) : null);

// ───────── 연·월(month 모드 — 이력서 경력 기간) ─────────

/** `YYYY-MM` 또는 `YYYY-MM-DD…` → `YYYY-MM`. 형식이 틀리면 null(일까지 든 옛 값도 그 달로 읽는다) */
export const monthKeyOf = (value?: string | null): string | null => {
  const match = /^(\d{4})-(\d{2})/.exec(value ?? '');
  if (!match) return null;
  const month: number = Number(match[2]);
  return month >= 1 && month <= 12 ? `${match[1]}-${match[2]}` : null;
};

/** 연·월 → `YYYY-MM` */
export const monthKey = (year: number, month: number): string => `${year}-${pad2(month)}`;

/** Date → 그 달 `YYYY-MM` (로컬) */
export const monthKeyOfDate = (date: Date): string => monthKey(date.getFullYear(), date.getMonth() + 1);

/** `YYYY-MM…` → `2023.05` (입력 칸·목록 표시). 형식이 틀리면 빈 글자 */
export const formatMonthDot = (value?: string | null): string => {
  const key: string | null = monthKeyOf(value);
  return key ? key.replace('-', '.') : '';
};

/** `YYYY-MM…` → `2023년 5월` (시트 요약 줄) */
export const formatMonthKo = (value?: string | null): string => {
  const key: string | null = monthKeyOf(value);
  return key ? `${key.slice(0, 4)}년 ${Number(key.slice(5, 7))}월` : '';
};

/** 그 달이 하한·상한(`YYYY-MM`, 둘 다 포함) 안인지 — `YYYY-MM` 문자열은 사전순 = 시간순 */
export const isMonthAllowed = (key: string, minMonth?: string | null, maxMonth?: string | null): boolean =>
  (!minMonth || key >= minMonth) && (!maxMonth || key <= maxMonth);
